
import { supabase } from '@/integrations/supabase/client';

interface CartItem {
  id?: string;
  user_id: string;
  product_id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

/**
 * Get cart items for a user from Supabase
 */
export const getCartItemsFromSupabase = async (userId: string): Promise<CartItem[]> => {
  try {
    if (!userId) return [];
    
    const { data, error } = await supabase
      .from('cart_items')
      .select('*')
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error fetching cart items:', error);
      return [];
    }
    
    return data as CartItem[] || [];
  } catch (error) {
    console.error('Error in getCartItemsFromSupabase:', error);
    return [];
  }
};

/**
 * Add an item to the cart in Supabase with upsert to prevent duplicates
 */
export const addToCartSupabase = async (userId: string, item: Omit<CartItem, 'user_id'>): Promise<boolean> => {
  try {
    if (!userId) return false;
    
    // Use upsert to handle duplicates automatically
    const { data, error } = await supabase
      .from('cart_items')
      .upsert({
        user_id: userId,
        product_id: item.product_id,
        name: item.name,
        price: item.price,
        image: item.image,
        quantity: item.quantity
      }, {
        onConflict: 'user_id,product_id',
        ignoreDuplicates: false
      })
      .select();
    
    if (error) {
      // If upsert fails due to no unique constraint, fall back to manual check
      console.log('Upsert failed, falling back to manual dedup:', error.message);
      
      // Get existing item
      const { data: existingItems, error: checkError } = await supabase
        .from('cart_items')
        .select('*')
        .eq('user_id', userId)
        .eq('product_id', item.product_id)
        .limit(1);
      
      if (checkError) {
        console.error('Error checking existing cart items:', checkError);
        return false;
      }
      
      if (existingItems && existingItems.length > 0) {
        // Update existing item quantity
        const existingItem = existingItems[0];
        const newQuantity = existingItem.quantity + item.quantity;
        
        const { error: updateError } = await supabase
          .from('cart_items')
          .update({ quantity: newQuantity })
          .eq('id', existingItem.id);
        
        if (updateError) {
          console.error('Error updating cart item:', updateError);
          return false;
        }
        
        // Clean up any other duplicates
        await supabase
          .from('cart_items')
          .delete()
          .eq('user_id', userId)
          .eq('product_id', item.product_id)
          .neq('id', existingItem.id);
      } else {
        // Insert new item
        const { error: insertError } = await supabase
          .from('cart_items')
          .insert({
            user_id: userId,
            product_id: item.product_id,
            name: item.name,
            price: item.price,
            image: item.image,
            quantity: item.quantity
          });
        
        if (insertError) {
          console.error('Error inserting cart item:', insertError);
          return false;
        }
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error in addToCartSupabase:', error);
    return false;
  }
};

/**
 * Remove an item from the cart in Supabase
 */
export const removeFromCartSupabase = async (userId: string, productId: number): Promise<boolean> => {
  try {
    if (!userId) return false;
    
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', userId)
      .eq('product_id', productId);
    
    if (error) {
      console.error('Error removing cart item:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in removeFromCartSupabase:', error);
    return false;
  }
};

/**
 * Update cart item quantity in Supabase
 */
export const updateCartItemQuantitySupabase = async (
  userId: string,
  productId: number,
  quantity: number
): Promise<boolean> => {
  try {
    if (!userId || quantity < 1) return false;
    
    const { error } = await supabase
      .from('cart_items')
      .update({ quantity })
      .eq('user_id', userId)
      .eq('product_id', productId);
    
    if (error) {
      console.error('Error updating cart item quantity:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in updateCartItemQuantitySupabase:', error);
    return false;
  }
};

/**
 * Clear all items from the cart in Supabase
 */
export const clearCartSupabase = async (userId: string): Promise<boolean> => {
  try {
    if (!userId) return false;
    
    const { error } = await supabase
      .from('cart_items')
      .delete()
      .eq('user_id', userId);
    
    if (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in clearCartSupabase:', error);
    return false;
  }
};
